import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { Container, Table, Card, Button, Dropdown, DropdownButton, Tab, Nav, Row, Col, Modal as RBModal, Form, Pagination, Badge, FloatingLabel, Modal, Accordion, InputGroup, ProgressBar } from 'react-bootstrap';
import { Alert } from 'react-bootstrap';
import ResidentPromotionManager from './components/ResidentPromotionManager';
import DelegationsManager from './components/DelegationsManager';
import { API_ENDPOINTS } from './config/api';
import ProgramPGYUtils from './utils/programPGYUtils';
import PageTitle from './components/PageTitle';
import UserAvatar from './components/UserAvatar';

// Add styles for sortable headers
const styles = {
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    padding: '0.75rem',
    verticalAlign: 'middle',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6'
  },
  sortIcon: {
    marginLeft: '4px',
    fontSize: '0.875rem',
    color: '#6c757d'
  }
};

const isProfileComplete = (user) => {
  if (!user) return false;
  if (user.role === 'resident') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.year &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  if (user.role === 'attending') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.specialty &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  if (user.role === 'medical student') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.yearOfSchool &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  if (user.role === 'administrative assistant') {
    return (
      user.firstName &&
      user.lastName &&
      user.email &&
      user.dob &&
      user.phone &&
      user.address
    );
  }
  return (
    user.firstName &&
    user.lastName &&
    user.email
  );
};

const isProfileSetupTokenExpired = (user) => {
  if (!user?.profileSetupTokenExpiry) return true;
  return new Date(user.profileSetupTokenExpiry) <= new Date();
};

const canResendExistingProfileInvite = (user) => {
  if (!user) return false;
  if (typeof user.canResendExistingProfileInvite === 'boolean') {
    return user.canResendExistingProfileInvite;
  }
  return !isProfileComplete(user) && !isProfileSetupTokenExpired(user);
};

const canResendNewProfileInvite = (user) => {
  if (!user) return false;
  if (typeof user.canResendNewProfileInvite === 'boolean') {
    return user.canResendNewProfileInvite;
  }
  return !isProfileComplete(user) && isProfileSetupTokenExpired(user);
};

const formatLastLogin = (lastLogin) => {
  const date = new Date(lastLogin);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

const specialties = [
  'General surgery',
  'Pediatric surgery',
  'Colorectal surgery',
  'Hepato-biliary surgery',
  'Oncologic surgery',
  'Burn surgery',
  'Hernia surgery',
  'Elective surgery',
  'Acute care surgery',
  'Trauma surgery',
  'Bariatric surgery',
  'Surgical ICU',
  'HENT surgery',
  'Renal surgery',
  'Transplant surgery',
  'Vascular surgery',
  'Plastic surgery'
];

// Helper to get initial tab and role from hash
function getInitialTabsFromHash() {
  const hash = window.location.hash.replace('#', '');
  if (hash.startsWith('users-')) {
    const role = hash.split('-')[1];
    return { tab: 'users', role: role || 'resident' };
  }
  if (hash === 'Promotion' || hash === 'promotion') return { tab: 'Promotion', role: 'resident' };
  if (hash === 'announcements') return { tab: 'announcements', role: 'resident' };
  if (hash === 'settings') return { tab: 'settings', role: 'resident' };
  if (hash === 'delegations') return { tab: 'delegations', role: 'resident' };
  return { tab: 'users', role: 'resident' };
}

const UsersPage = () => {
  const initialTabs = getInitialTabsFromHash();
  const [activeTab, setActiveTab] = useState(initialTabs.tab);
  const [activeRoleTab, setActiveRoleTab] = useState(initialTabs.role);
  const [users, setUsers] = useState([]);
  const [graduatedUsers, setGraduatedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [pageTitle] = useState("Users");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteRole, setInviteRole] = useState('resident');
  // Document assignment state
  const [assignDocuments, setAssignDocuments] = useState(false);
  const [selectedDocumentItems, setSelectedDocumentItems] = useState({ packages: [], documents: [] });
  const [inviteStep, setInviteStep] = useState(1); // 1: Basic Info, 2: Document Assignment
  const [packages, setPackages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);



  
  const [inviteForm, setInviteForm] = useState({ 
    firstName: '', 
    middleName: '', 
    lastName: '', 
    email: '', 
    year: '', 
    yearOfSchool: '', 
    specialty: '', 
    department: '', 
    positionTitle: '', 
    boardCertified: false, 
    startDate: '', 
    endDate: '',
    academicYearStart: '',
    graduationYear: ''
  });
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [resendingInvite, setResendingInvite] = useState(null);
  const [resendAlert, setResendAlert] = useState(null);
  const navigate = useNavigate();
  const [roleFilter, setRoleFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch program details and PGY levels

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    startDate: '',
    endDate: '',
    type: 'general'
  });
  const [announcementError, setAnnouncementError] = useState('');
  const [announcementSuccess, setAnnouncementSuccess] = useState('');
  const [isSubmittingAnnouncement, setIsSubmittingAnnouncement] = useState(false);
  
  // Case Log Feedback Settings state
  const [feedbackSettings, setFeedbackSettings] = useState({
    feedbackTemplateId: null,
    rolesToTrigger: ['Chief Resident', 'Primary Resident'],
    isEnabled: true
  });
  const [feedbackTemplates, setFeedbackTemplates] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');

  // Add state for showing the confirm modal and the action to confirm
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState('Are you sure?');

  // Program details state
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // PGY level state
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);

  // Load saved state from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem('usersPageState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        if (parsedState.roleFilter) setRoleFilter(parsedState.roleFilter);
        if (parsedState.searchTerm) setSearchTerm(parsedState.searchTerm);
        if (parsedState.currentPage) setCurrentPage(parsedState.currentPage);
        if (parsedState.sortConfig) setSortConfig(parsedState.sortConfig);
        if (parsedState.itemsPerPage) setItemsPerPage(parsedState.itemsPerPage);
      } catch (error) {
        console.error('Error loading saved state:', error);
      }
    }
  }, []);

  // Fetch program details
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  // Add debugging for pgyLevelOptions state
  useEffect(() => {
    console.log('Current pgyLevelOptions:', pgyLevelOptions);
  }, [pgyLevelOptions]);

  // Fetch program details on component mount
  useEffect(() => {
    fetchProgramDetails();
  }, []);

  // Fetch program details on component mount
  useEffect(() => {
    fetchProgramDetails();
  }, []);

  // Fetch subscription data when subscriptions tab is active
  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptionData();
    }
  }, [activeTab]);

  const fetchSubscriptionData = async () => {
    setSubscriptionLoading(true);
    try {
      const isMulti = programDetails?.selectedPlan === 'multi';
      const monthlyPrice = isMulti ? 599 : 299;
      const planName = isMulti ? 'Multi Program' : 'Single Program';
      const residentCount = users.filter((u) => u.role === 'resident').length;
      const facultyCount = users.filter((u) => u.role === 'attending').length;
      const formattedAmount = `$${monthlyPrice.toFixed(2)}`;

      const mockSubscriptionData = {
        plan: {
          name: planName,
          cadence: 'Monthly',
          price: `$${monthlyPrice}/month`,
          status: 'Active',
          renewalDate: 'Jul 15, 2026',
          seatsIncluded: isMulti ? 100 : 50,
        },
        usage: {
          residents: residentCount,
          residentLimit: isMulti ? 100 : 50,
          faculty: facultyCount,
          facultyLimit: isMulti ? 30 : 20,
          storageUsedGb: 2.5,
          storageLimitGb: 10,
        },
        paymentMethod: {
          brand: 'Visa',
          last4: '4242',
          expMonth: '12',
          expYear: '2028',
          billingEmail: programDetails?.director?.email || 'billing@program.org',
        },
        billingContact: {
          programName: programDetails?.name || 'Program Name',
          institutionName: programDetails?.Institution?.name || 'Institution Name',
          contactName: programDetails?.director
            ? `${programDetails.director.firstName} ${programDetails.director.lastName}`
            : 'Program Director',
          email: programDetails?.director?.email || 'director@program.org',
          phone: programDetails?.director?.phone || '(555) 217-4932',
          address: programDetails?.address || 'Address not on file',
        },
        invoices: [
          {
            id: 'INV-2026-004',
            date: 'Jun 01, 2026',
            amount: formattedAmount,
            status: 'Paid',
            issueDate: 'Jun 01, 2026',
            dueDate: 'Jun 15, 2026',
            invoiceFor: `${planName} Subscription`,
            issuedBy: 'Medhive Resident Management Platform LLC',
            paymentTerm: '14 days',
            project: programDetails?.name || 'Residency Program',
            items: [
              { description: 'Base platform subscription', quantity: 1, rate: formattedAmount, amount: formattedAmount },
            ],
          },
          {
            id: 'INV-2026-003',
            date: 'May 01, 2026',
            amount: formattedAmount,
            status: 'Paid',
            issueDate: 'May 01, 2026',
            dueDate: 'May 15, 2026',
            invoiceFor: `${planName} Subscription`,
            issuedBy: 'Medhive Resident Management Platform LLC',
            paymentTerm: '14 days',
            project: programDetails?.name || 'Residency Program',
            items: [
              { description: 'Base platform subscription', quantity: 1, rate: formattedAmount, amount: formattedAmount },
            ],
          },
          {
            id: 'INV-2026-002',
            date: 'Apr 01, 2026',
            amount: formattedAmount,
            status: 'Open',
            issueDate: 'Apr 01, 2026',
            dueDate: 'Apr 15, 2026',
            invoiceFor: `${planName} Subscription`,
            issuedBy: 'Medhive Resident Management Platform LLC',
            paymentTerm: '14 days',
            project: programDetails?.name || 'Residency Program',
            items: [
              { description: 'Base platform subscription', quantity: 1, rate: formattedAmount, amount: formattedAmount },
            ],
          },
          {
            id: 'INV-2026-001',
            date: 'Mar 01, 2026',
            amount: formattedAmount,
            status: 'Paid',
            issueDate: 'Mar 01, 2026',
            dueDate: 'Mar 15, 2026',
            invoiceFor: `${planName} Subscription`,
            issuedBy: 'Medhive Resident Management Platform LLC',
            paymentTerm: '14 days',
            project: programDetails?.name || 'Residency Program',
            items: [
              { description: 'Base platform subscription', quantity: 1, rate: formattedAmount, amount: formattedAmount },
            ],
          },
        ],
        upcomingPayment: {
          amount: formattedAmount,
          chargeDate: 'Jul 01, 2026',
          method: 'Visa ending in 4242',
          invoiceId: 'INV-2026-005',
        },
      };

      setSubscriptionData(mockSubscriptionData);
    } catch (err) {
      console.error("Failed to fetch subscription data", err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const subscriptionPercents = useMemo(() => {
    if (!subscriptionData?.usage) {
      return { residents: 0, faculty: 0, storage: 0 };
    }
    const { usage } = subscriptionData;
    return {
      residents: Math.min(Math.round((usage.residents / usage.residentLimit) * 100), 100),
      faculty: Math.min(Math.round((usage.faculty / usage.facultyLimit) * 100), 100),
      storage: Math.min(Math.round((usage.storageUsedGb / usage.storageLimitGb) * 100), 100),
    };
  }, [subscriptionData]);

  const filteredSubscriptionInvoices = useMemo(() => {
    if (!subscriptionData?.invoices) return [];
    const search = invoiceSearch.trim().toLowerCase();
    return subscriptionData.invoices.filter((invoice) => {
      const statusMatches = invoiceStatusFilter === 'all' || invoice.status.toLowerCase() === invoiceStatusFilter;
      const searchMatches =
        !search ||
        invoice.id.toLowerCase().includes(search) ||
        invoice.date.toLowerCase().includes(search) ||
        invoice.amount.toLowerCase().includes(search);
      return statusMatches && searchMatches;
    });
  }, [subscriptionData, invoiceSearch, invoiceStatusFilter]);

  const invoiceStatusVariant = {
    paid: 'success',
    open: 'warning',
    failed: 'danger',
  };

  const openInvoiceModal = (invoice) => setSelectedInvoice(invoice);
  const closeInvoiceModal = () => setSelectedInvoice(null);

  // Save state to localStorage whenever relevant states change
  useEffect(() => {
    const stateToSave = {
      roleFilter,
      searchTerm,
      currentPage,
      sortConfig,
      itemsPerPage
    };
    localStorage.setItem('usersPageState', JSON.stringify(stateToSave));
  }, [roleFilter, searchTerm, currentPage, sortConfig, itemsPerPage]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Function to clear saved state
  const clearSavedState = () => {
    localStorage.removeItem('usersPageState');
    setRoleFilter('');
    setSearchTerm('');
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setItemsPerPage(10);
  };

  // Helper function to calculate graduation year based on PGY level and academic year start
  const calculateGraduationYear = (pgyYear, academicYearStart) => {
    if (!pgyYear || !academicYearStart || !/^\d{4}/.test(academicYearStart)) return '';
    const startYear = parseInt(academicYearStart.split('-')[0]);
    // Calculate how many years remaining from current PGY level to completion
    const yearsRemaining = maxPGYLevel - parseInt(pgyYear);
    // Add the remaining years to get graduation year (they graduate at the END of their final year)
    return startYear + yearsRemaining + 1;
  };

  // Helper function to get current academic year start date
  const getCurrentAcademicYearStart = () => {
    const now = new Date();
    const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1; // If after June, use current year, else previous year
    return `${year}-07-01`;
  };

  // Helper function to calculate academic year start date based on PGY level
  const calculateAcademicYearStart = (pgyYear) => {
    if (!pgyYear) return '';
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11
    
    // Calculate how many years back from current academic year
    // PGY-1 starts in current academic year, PGY-2 started 1 year ago, etc.
    const yearsBack = parseInt(pgyYear) - 1;
    
    // Determine the academic year start
    // If we're in July-December, use current year, else use previous year
    const academicYearStart = currentMonth >= 6 ? currentYear : currentYear - 1;
    
    // Subtract the years back to get the actual start year
    const startYear = academicYearStart - yearsBack;
    
    return `${startYear}-07-01`;
  };

  const roleTabs = [
    { key: 'resident', label: 'Residents' },
    { key: 'graduated', label: 'Graduated' },
    { key: 'attending', label: 'Attendings' },
    { key: 'medical student', label: 'Medical Students' },
    { key: 'administrative assistant', label: 'Administrative Assistants' },
  ];

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Please log in to view users");
        setIsLoading(false);
        return;
      }

      try {
        // Fetch both active users and graduated users
        const [activeUsersRes, graduatedUsersRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.DIRECTOR}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_ENDPOINTS.DIRECTOR}/graduated-users`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ]);

        if (!activeUsersRes.ok) {
          throw new Error('Failed to fetch active users');
        }

        const activeData = await activeUsersRes.json();
        setUsers(activeData);
        
        // Handle graduated users (don't fail if endpoint doesn't exist yet)
        let graduatedData = [];
        if (graduatedUsersRes.ok) {
          graduatedData = await graduatedUsersRes.json();
          setGraduatedUsers(graduatedData);
        } else {
          console.log('Graduated users endpoint not available yet, setting empty array');
          setGraduatedUsers([]);
        }
        
      } catch (err) {
        console.error("Failed to fetch users", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fetch settings when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      fetchFeedbackSettings();
      fetchFeedbackTemplates();
    }
  }, [activeTab]);

  // Helper to get filtered users for the active tab
  const getFilteredUsers = () => {
    let filtered;
    
    if (activeRoleTab === 'graduated') {
      // For graduated tab, use graduatedUsers array
      filtered = graduatedUsers.filter(u => u.role === 'resident');
    } else {
      // For other tabs, use regular users array
      filtered = users.filter(u => u.role === activeRoleTab);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.firstName.toLowerCase().includes(term) ||
        user.lastName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      );
    }
    
    if (activeRoleTab === 'resident' && roleFilter) {
      filtered = filtered.filter(u => String(u.year) === roleFilter);
    }
    if (activeRoleTab === 'graduated' && roleFilter) {
      filtered = filtered.filter(u => String(u.year) === roleFilter);
    }
    if (activeRoleTab === 'attending' && roleFilter) {
      filtered = filtered.filter(u => u.specialty === roleFilter);
    }
    if (activeRoleTab === 'medical student' && roleFilter) {
      filtered = filtered.filter(u => String(u.yearOfSchool) === roleFilter);
    }
    return filtered;
  };

  // Whenever tab or filter changes, reset page
  useEffect(() => {
    setCurrentPage(1);
  }, [activeRoleTab, roleFilter]);

  const filtered = getFilteredUsers();
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, activeRoleTab]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filtered].sort((a, b) => {
      if (key === 'name') {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return direction === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      }
      if (key === 'email') {
        return direction === 'asc' 
          ? a.email.toLowerCase().localeCompare(b.email.toLowerCase())
          : b.email.toLowerCase().localeCompare(a.email.toLowerCase());
      }
      if (key === 'year') {
        return direction === 'asc' ? a.year - b.year : b.year - a.year;
      }
      if (key === 'registrationStatus') {
        const statusA = isProfileComplete(a);
        const statusB = isProfileComplete(b);
        return direction === 'asc' ? statusA - statusB : statusB - statusA;
      }
      if (key === 'lastLogin') {
        const dateA = a.lastLogin ? new Date(a.lastLogin) : new Date(0);
        const dateB = b.lastLogin ? new Date(b.lastLogin) : new Date(0);
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });

    setUsers(sorted);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const fetchOnboardingData = async () => {
    try {
      setLoadingDocuments(true);
      const token = localStorage.getItem('token');
      
      // Fetch packages and documents
      const [packagesRes, documentsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.ONBOARDING}/packages`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (packagesRes.ok && documentsRes.ok) {
        const packagesData = await packagesRes.json();
        const documentsData = await documentsRes.json();
        setPackages(packagesData);
        setDocuments(documentsData);
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentAssignmentStep = () => {
    if (assignDocuments) {
      fetchOnboardingData();
      setInviteStep(2);
    } else {
      // Skip document assignment, proceed with invite
      handleInviteSubmit();
    }
  };

  const handleNextStep = () => {
    if (assignDocuments) {
      fetchOnboardingData();
      setInviteStep(2);
    } else {
      // Skip document assignment, proceed with invite
      handleInviteSubmit();
    }
  };

  const handleBackToBasicInfo = () => {
    setInviteStep(1);
  };

  const handlePackageToggle = (packageId) => {
    setSelectedDocumentItems(prev => ({
      ...prev,
      packages: prev.packages.includes(packageId) 
        ? prev.packages.filter(id => id !== packageId)
        : [...prev.packages, packageId]
    }));
  };

  const handleDocumentToggle = (documentId) => {
    setSelectedDocumentItems(prev => ({
      ...prev,
      documents: prev.documents.includes(documentId) 
        ? prev.documents.filter(id => id !== documentId)
        : [...prev.documents, documentId]
    }));
  };

  const handleInviteSubmit = async () => {
    setInviteError('');
    setInviteSuccess('');
    // Validation
    if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.email) {
      setInviteError('First name, last name, and email are required.');
      return;
    }
    if (inviteRole === 'resident' && !inviteForm.year) {
      setInviteError('PGY year is required for residents.');
      return;
    }
    if (inviteRole === 'medical student' && !inviteForm.yearOfSchool) {
      setInviteError('Year of school is required for medical students.');
      return;
    }
    if (inviteRole === 'attending' && !inviteForm.specialty) {
      setInviteError('Specialty is required.');
      return;
    }
    if (inviteRole === 'attending' && !inviteForm.department) {
      setInviteError('Department is required.');
      return;
    }
    if (inviteRole === 'attending' && !inviteForm.positionTitle) {
      setInviteError('Position title is required.');
      return;
    }
    if (inviteRole === 'attending' && !inviteForm.startDate) {
      setInviteError('Start date is required.');
      return;
    }
    
    // Add document requirements if assigned
    if (inviteRole === 'resident' && assignDocuments) {
      const documentRequirements = {
        packages: selectedDocumentItems.packages,
        documents: selectedDocumentItems.documents
      };
      if (documentRequirements.packages.length === 0 && documentRequirements.documents.length === 0) {
        setInviteError('Please select at least one document or package to assign.');
        return;
      }
    }
    
    const body = {
      firstName: inviteForm.firstName,
      middleName: inviteForm.middleName,
      lastName: inviteForm.lastName,
      email: inviteForm.email,
      role: inviteRole,
    };
    if (inviteRole === 'resident') {
      body.year = inviteForm.year;
      body.academicYearStart = inviteForm.academicYearStart;
      body.graduationYear = inviteForm.graduationYear;
      
      // Add document requirements if assigned
      if (assignDocuments) {
        body.documentRequirements = {
          packages: selectedDocumentItems.packages,
          documents: selectedDocumentItems.documents
        };
      }
    } else if (inviteRole === 'medical student') {
      body.yearOfSchool = inviteForm.yearOfSchool;
    } else if (inviteRole === 'attending') {
      body.specialty = inviteForm.specialty;
      body.department = inviteForm.department;
      body.positionTitle = inviteForm.positionTitle;
      body.boardCertified = inviteForm.boardCertified;
      body.startDate = inviteForm.startDate;
      if (inviteForm.endDate) body.endDate = inviteForm.endDate;
    }
    const token = localStorage.getItem('token');
    // Get programId from user context or localStorage (assume it's stored after login)
    const programId = localStorage.getItem('programId');
    if (!token) {
      setInviteError('You must be logged in.');
      return;
    }
    if (programId) {
      body.programId = parseInt(programId, 10);
    }
    try {
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setInviteSuccess('✅ Invitation sent!');
        setInviteForm({ firstName: '', middleName: '', lastName: '', email: '', year: '', yearOfSchool: '', specialty: '', department: '', positionTitle: '', boardCertified: false, startDate: '', endDate: '', academicYearStart: '', graduationYear: '' });
        setAssignDocuments(false);
        setSelectedDocumentItems({ packages: [], documents: [] });
        setInviteStep(1);
        setTimeout(() => {
          setShowInviteModal(false);
          setInviteSuccess('');
          window.location.reload();
        }, 1200);
      } else {
        setInviteError(data.message || 'Failed to invite user.');
      }
    } catch (err) {
      setInviteError('Server error.');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    setDeleteError('');
    
    const token = localStorage.getItem("token");
    if (!token) {
      setDeleteError('You must be logged in.');
      setIsDeleting(false);
      return;
    }

    try {
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/delete-user/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      
      if (res.ok) {
        setShowDeleteModal(false);
        setUserToDelete(null);
        // Remove the user from the local state
        setUsers(users.filter(user => user.id !== userToDelete.id));
      } else {
        setDeleteError(data.message || 'Failed to delete user.');
      }
    } catch (err) {
      setDeleteError('Server error occurred.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendProfileInvite = async (user, mode) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setResendAlert({ variant: 'danger', message: 'You must be logged in.' });
      return;
    }

    setResendingInvite({ userId: user.id, mode });
    setResendAlert(null);

    try {
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/users/${user.id}/resend-profile-invite`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();

      if (res.ok) {
        const expiry = data.profileSetupTokenExpiry;
        const expired = expiry ? new Date(expiry) <= new Date() : true;
        setUsers((prev) =>
          prev.map((u) =>
            u.id === user.id
              ? {
                  ...u,
                  profileSetupTokenExpiry: expiry,
                  profileCompleted: false,
                  canResendExistingProfileInvite: !expired,
                  canResendNewProfileInvite: expired,
                  canResendProfileInvite: true,
                }
              : u
          )
        );
        setResendAlert({
          variant: 'success',
          message: data.message || `Invitation email resent to ${user.email}.`,
        });
      } else {
        setResendAlert({ variant: 'danger', message: data.message || 'Failed to resend invitation.' });
      }
    } catch (err) {
      setResendAlert({ variant: 'danger', message: 'Server error. Please try again.' });
    } finally {
      setResendingInvite(null);
    }
  };

  // Helper function to capitalize names
  const capitalizeName = (name) => {
    return name.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Fetch announcements
  useEffect(() => {
    if (activeTab === 'announcements') {
      fetchAnnouncements();
    }
  }, [activeTab]);

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error("Failed to fetch announcements", error);
    }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingAnnouncement(true);
    setAnnouncementError('');
    setAnnouncementSuccess('');

    try {
      const token = localStorage.getItem("token");
      const url = editingAnnouncement 
        ? `${API_ENDPOINTS.ANNOUNCEMENTS}/${editingAnnouncement.id}`
        : `${API_ENDPOINTS.ANNOUNCEMENTS}`;
      
      const method = editingAnnouncement ? 'PUT' : 'POST';

      // Ensure dates are properly formatted to avoid timezone issues
      const formData = {
        ...announcementForm,
        startDate: announcementForm.startDate || '',
        endDate: announcementForm.endDate || ''
      };

      console.log('Sending announcement data:', formData);
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setAnnouncementSuccess(
          editingAnnouncement 
            ? 'Announcement updated successfully!' 
            : 'Announcement created successfully!'
        );
        setShowAnnouncementModal(false);
        resetAnnouncementForm();
        fetchAnnouncements();
      } else {
        const errorData = await response.json();
        setAnnouncementError(errorData.message || 'Failed to save announcement');
      }
    } catch (error) {
      setAnnouncementError('Failed to save announcement');
    } finally {
      setIsSubmittingAnnouncement(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    setConfirmMessage('Are you sure you want to delete this announcement? This action cannot be undone.');
    setConfirmAction(() => async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_ENDPOINTS.ANNOUNCEMENTS}/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          fetchAnnouncements();
        }
      } catch (error) {
        console.error("Failed to delete announcement", error);
      }
    });
    setShowConfirmModal(true);
  };

  const editAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    
    // Helper function to format date for input field
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // If it's an ISO string, extract the date part
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      // If it's a different format, try to parse it
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch (e) {
        return '';
      }
    };
    
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      startDate: formatDateForInput(announcement.startDate),
      endDate: formatDateForInput(announcement.endDate),
      type: announcement.type
    });
    setShowAnnouncementModal(true);
  };

  const resetAnnouncementForm = () => {
    setAnnouncementForm({
      title: '',
      content: '',
      startDate: '',
      endDate: '',
      type: 'general'
    });
    setEditingAnnouncement(null);
    setAnnouncementError('');
    setAnnouncementSuccess('');
  };

  // Case Log Feedback Settings functions
  const fetchFeedbackSettings = async () => {
    setSettingsLoading(true);
    setSettingsError('');
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/case-log-feedback-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch settings');
      }
      
      const data = await res.json();
      console.log('🔍 Fetched feedback settings:', data);
      console.log('🔍 Fetched rolesToTrigger:', data.rolesToTrigger);
      setFeedbackSettings(data);
    } catch (err) {
      console.error('Error fetching feedback settings:', err);
      setSettingsError('Failed to load settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchFeedbackTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/feedback-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch templates');
      }
      
      const data = await res.json();
      setFeedbackTemplates(data);
    } catch (err) {
      console.error('Error fetching feedback templates:', err);
    }
  };

  const saveFeedbackSettings = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsSuccess('');
    
    try {
      const token = localStorage.getItem("token");
      
      // Debug: Log what we're about to send
      console.log('🔍 About to send feedback settings:', feedbackSettings);
      console.log('🔍 rolesToTrigger:', feedbackSettings.rolesToTrigger);
      
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/case-log-feedback-settings`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(feedbackSettings)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('❌ Backend error response:', errorData);
        throw new Error(errorData.message || 'Failed to save settings');
      }
      
      const data = await res.json();
      console.log('✅ Backend success response:', data);
      setFeedbackSettings(data.settings);
      setSettingsSuccess('Settings saved successfully');
    } catch (err) {
      console.error('Error saving feedback settings:', err);
      setSettingsError(err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleRoleToggle = (role) => {
    setFeedbackSettings(prev => ({
      ...prev,
      rolesToTrigger: prev.rolesToTrigger.includes(role)
        ? prev.rolesToTrigger.filter(r => r !== role)
        : [...prev.rolesToTrigger, role]
    }));
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'urgent':
        return 'danger';
      case 'important':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'primary';
    }
  };

  const formatDate = (dateString) => {
    // Handle date strings without timezone conversion
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, parse it safely
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    // Fallback to original method for other formats
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Hash navigation logic
  useEffect(() => {
    function handleHashChange() {
      const hash = window.location.hash.replace('#', '');
      if (hash.startsWith('users-')) {
        setActiveTab('users');
        const role = hash.split('-')[1];
        if (roleTabs.some(tab => tab.key === role)) {
          setActiveRoleTab(role);
        }
      } else if (hash.startsWith('promotion-')) {
        setActiveTab('Promotion');
      } else if (hash === 'users') {
        setActiveTab('users');
      } else if (hash === 'promotion') {
        setActiveTab('Promotion');
      } else if (hash === 'announcements') {
        setActiveTab('announcements');
      } else if (hash === 'delegations') {
        setActiveTab('delegations');
      } else if (hash === 'subscriptions') {
        setActiveTab('subscriptions');
      }
    }
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // run on mount
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Update hash when main tab changes
  useEffect(() => {
    if (activeTab === 'users') {
      window.location.hash = `users-${activeRoleTab}`;
    } else if (activeTab === 'Promotion') {
      window.location.hash = 'promotion';
    } else if (activeTab === 'announcements') {
      window.location.hash = 'announcements';
    } else if (activeTab === 'delegations') {
      window.location.hash = 'delegations';
    } else if (activeTab === 'subscriptions') {
      window.location.hash = 'subscriptions';
    }
  }, [activeTab, activeRoleTab]);

  const getFileIcon = (fileName, mimeType) => {
    const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
    const type = mimeType ? mimeType.toLowerCase() : '';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension) || type.includes('image/')) {
      return 'bi bi-image-fill text-info';
    }
    
    // PDF files
    if (extension === 'pdf' || type.includes('pdf')) {
      return 'bi bi-file-earmark-pdf-fill text-danger';
    }
    
    // Word documents
    if (['doc', 'docx'].includes(extension) || type.includes('word') || type.includes('document')) {
      return 'bi bi-file-earmark-word-fill text-primary';
    }
    
    // Excel files
    if (['xls', 'xlsx'].includes(extension) || type.includes('excel') || type.includes('spreadsheet')) {
      return 'bi bi-file-earmark-excel-fill text-success';
    }
    
    // PowerPoint files
    if (['ppt', 'pptx'].includes(extension) || type.includes('powerpoint') || type.includes('presentation')) {
      return 'bi bi-file-earmark-ppt-fill text-warning';
    }
    
    // Text files
    if (['txt', 'rtf'].includes(extension) || type.includes('text/')) {
      return 'bi bi-file-earmark-text-fill text-secondary';
    }
    
    // Default document icon
    return 'bi bi-file-earmark-text-fill text-warning';
  };

  const getFileIconBg = (fileName, mimeType) => {
    const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
    const type = mimeType ? mimeType.toLowerCase() : '';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension) || type.includes('image/')) {
      return 'bg-info';
    }
    
    // PDF files
    if (extension === 'pdf' || type.includes('pdf')) {
      return 'bg-danger';
    }
    
    // Word documents
    if (['doc', 'docx'].includes(extension) || type.includes('word') || type.includes('document')) {
      return 'bg-primary';
    }
    
    // Excel files
    if (['xls', 'xlsx'].includes(extension) || type.includes('excel') || type.includes('spreadsheet')) {
      return 'bg-success';
    }
    
    // PowerPoint files
    if (['ppt', 'pptx'].includes(extension) || type.includes('powerpoint') || type.includes('presentation')) {
      return 'bg-warning';
    }
    
    // Text files
    if (['txt', 'rtf'].includes(extension) || type.includes('text/')) {
      return 'bg-secondary';
    }
    
    // Default document icon
    return 'bg-warning';
  };
  
  const getPGYBadgeColor = (pgy) => {
    const colors = {
      1: 'primary',
      2: 'success',
      3: 'warning',
      4: 'info',
      5: 'secondary',
      6: 'dark',
      7: 'danger'
    };
    return colors[pgy] || 'light';
  };

  return (
    <div className="min-vh-100">
      <PageTitle title="User Management" description="Manage users, resident progression, announcements, and program settings" />
      <Navbar pageTitle={pageTitle} />

      {/* Program Details Section */}
      {programDetails && (
        <div className="bg-white shadow-sm" style={{borderBottom: '1px dashed var(--border-bg)'}}>
          <Container className="py-2">
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div className="me-3 bg-secondary rounded-3 py-1 px-3">
                    <i className="bi bi-hospital fs-3"></i>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-semibold">{programDetails.name}</h6>
                    <p className="mb-0 text-muted small">
                      {programDetails.specialty && `${programDetails.specialty} • `}
                      {programDetails.Institution?.name && `${programDetails.Institution.name}`}
                      {programDetails.duration && ` • ${programDetails.duration} years`}
                      {programDetails.residentCount && ` • ${programDetails.residentCount} residents`}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-md-end">
                <div className="d-flex flex-column align-items-md-end d-none d-md-block ">
                    <h6 className="fw-semibold mb-0">
                    {programDetails.director?.firstName} {programDetails.director?.lastName}
                  </h6>
                  <p className="mb-0 text-muted small">Program Director</p>
                
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}

      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col lg={2} md={6} sm={6} className=" d-flex col-6 order-first order-lg-1 order-md-1 order-sm-1 py-lg-0 py-md-3 py-3 align-items-center justify-content-start">
            <h6 className="mb-0 fw-semibold">Account Management</h6>
            </Col>
            <Col lg={8} md={12} sm={12} className="order-last order-lg-2 order-md-3 order-sm-3">

            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Nav variant="tabs" className="scrollable-tabs" style={{marginBottom: '-1px'}}>
            <Nav.Item style={{ flexShrink: 0 }}>
              <Nav.Link eventKey="users">Users</Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ flexShrink: 0 }}>
              <Nav.Link eventKey="delegations">Delegations</Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ flexShrink: 0 }}>
              <Nav.Link eventKey="Promotion">Resident Progression</Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ flexShrink: 0 }}>
              <Nav.Link eventKey="announcements">
                Announcements
              </Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ flexShrink: 0 }}>
              <Nav.Link eventKey="subscriptions">
                Subscriptions
              </Nav.Link>
            </Nav.Item>
            <Nav.Item style={{ flexShrink: 0 }}>
              <Nav.Link eventKey="settings">
                Settings
              </Nav.Link>
            </Nav.Item>
          </Nav>
          </Tab.Container>
            </Col>
            <Col lg={2} md={6} sm={6} className="d-flex col-6 order-lg-3 order-md-2 order-sm-2 py-lg-0 py-md-2 py-2 align-items-center justify-content-end">
               {activeTab === "users" && (
                                 <div className="d-flex">
            {/* Add User Button and Modal */}
              <DropdownButton
                id="add-user-dropdown"
                title="+ Add User"
                variant="outline-primary"
                size="sm"
                align="end"
                onSelect={(role) => {
                  setInviteRole(role);
                  setShowInviteModal(true);
                }}
              >
                <Dropdown.Item eventKey="resident">Resident</Dropdown.Item>
                <Dropdown.Item eventKey="administrative assistant">Administrative Assistant</Dropdown.Item>
                <Dropdown.Item eventKey="attending">Attending</Dropdown.Item>
                <Dropdown.Item eventKey="medical student">Medical Student</Dropdown.Item>
              </DropdownButton>
                </div>
               )}
               {activeTab === "announcements" && (
                 <div className="d-flex gap-3">
                   <Button
                     variant="primary"
                     size="sm"
                     onClick={() => {
                       resetAnnouncementForm();
                       setShowAnnouncementModal(true);
                     }}
                   >
                     <i className="bi bi-plus-lg me-1" />
                     Announcement
                   </Button>
                 </div>
               )}
               
            </Col>
          </Row>

        </Container>
      </div>


      <Container className="mt-4">
      <Tab.Content>
      <Tab.Pane eventKey="users" active={activeTab === "users"}>
        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading users...</p>
          </div>
        ) : (
          <>
            
            <div>
              <div>
                <Tab.Container activeKey={activeRoleTab} onSelect={setActiveRoleTab}>
                  {/* Mobile menu (shown on small screens) */}
                  <div className="d-md-none mb-3">
                    <Accordion defaultActiveKey={null}>
                      <Card className="bg-white shadow-sm border-0">
                        <Accordion.Item eventKey="0" className="border-0">
                          <Accordion.Header>Menu</Accordion.Header>
                          <Accordion.Body className="p-0">
                            <Nav variant="pills" className="flex-column">
                              {roleTabs.map((tab) => (
                                <Nav.Item key={`m-${tab.key}`}>
                                  <Nav.Link
                                    eventKey={tab.key}
                                    active={activeRoleTab === tab.key}
                                    onClick={() => setActiveRoleTab(tab.key)}
                                    className="px-3 py-2"
                                  >
                                    {tab.label}
                                  </Nav.Link>
                                </Nav.Item>
                              ))}
                            </Nav>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Card>
                    </Accordion>
                  </div>
                  <Row>
                    {/* Sidebar menu (hidden on small screens) */}
                    <Col lg={2} md={3} className="d-none d-md-block mb-3">
                      <Card className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top" style={{ top: '5rem' }}>
                        <Nav variant="pills" className="flex-column">
                          <Nav.Item>
                            <Nav.Link className="disabled fw-semibold">Menu</Nav.Link>
                          </Nav.Item>
                          {roleTabs.map((tab) => (
                            <Nav.Item key={tab.key}>
                              <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                            </Nav.Item>
                          ))}
                        </Nav>
                      </Card>
                    </Col>
                    <Col lg={10} md={9} sm={12} className="col-12">
                      <Tab.Content>
                        {roleTabs.map(tab => (
                          <Tab.Pane eventKey={tab.key} key={tab.key}>
                            {/* Filter Bar */}
                            <Card className="shadow-sm border-0">
                              <Card.Header className="border-0 px-4 py-3 d-flex flex-column flex-md-row justify-content-between">
                                <div className="d-flex flex-column justify-content-start align-items-start">
                                  <h6 className="mb-0 fw-semibold">{tab.label}</h6>
                                  <p className="text-muted small mb-0">showing {paginated.length} of {filtered.length} users</p>
                                </div>
                                <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                                
                                {tab.key === 'resident' && (
                                  <div className="d-flex align-items-center gap-2"> 
                                    {(searchTerm || roleFilter) && (
                                      <Button 
                                        variant="light-solid" 
                                        size="sm" 
                                        onClick={() => { setSearchTerm(''); setRoleFilter(''); }}
                                        className="d-flex align-items-center"
                                      >
                                        <i className="bi bi-x-lg"></i> <span className="d-none d-md-inline-block ms-1">clear</span>
                                      </Button>
                                    )}
                                    <Form.Group>
                                      <Form.Select value={roleFilter} size="sm" onChange={e => setRoleFilter(e.target.value)}>
                                        <option value="">All</option>
                                        {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    </Form.Group>
                                    <Form.Group>
                                      <Form.Control type="text" size="sm" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ minWidth: 180 }} />
                                    </Form.Group>
                              </div>
                            )}
                            {tab.key === 'graduated' && (
                              <div className="d-flex align-items-center gap-2"> 
                                 {(searchTerm || roleFilter) && (
                                  <Button 
                                    variant="light-solid" 
                                    size="sm" 
                                    onClick={() => { setSearchTerm(''); setRoleFilter(''); }}
                                    className="d-flex align-items-center"
                                  >
                                    <i className="bi bi-x-lg"></i> <span className="d-none d-md-inline-block ms-1">clear</span>
                                  </Button>
                                )}
                                <Form.Group>
                                  <Form.Select value={roleFilter} size="sm" onChange={e => setRoleFilter(e.target.value)}>
                                    <option value="">All</option>
                                    {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                  <Form.Control type="text" size="sm" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ minWidth: 180 }} />
                                </Form.Group>
                              </div>
                            )}
                            {tab.key === 'attending' && (
                              <div className="d-flex align-items-center gap-2">
                                 {(searchTerm || roleFilter) && (
                                  <Button 
                                    variant="light-solid" 
                                    size="sm" 
                                    onClick={() => { setSearchTerm(''); setRoleFilter(''); }}
                                    className="d-flex align-items-center"
                                  >
                                    <i className="bi bi-x-lg"></i> <span className="d-none d-md-inline-block ms-1">clear</span>
                                  </Button>
                                )}
                                <Form.Select value={roleFilter} size="sm" style={{ width: '130px' }} onChange={e => setRoleFilter(e.target.value)}>
                                  <option value="">All</option>
                                  {specialties.map(spec => (
                                    <option key={spec} value={spec}>{spec}</option>
                                  ))}
                                </Form.Select>
                                <Form.Group>
                                  <Form.Control
                                  type="text"
                                  placeholder="Search..."
                                  value={searchTerm}
                                  size="sm"
                                  onChange={e => setSearchTerm(e.target.value)}
                                />
                                </Form.Group>
                              </div>
                            )}
                            {tab.key === 'medical student' && (
                              <div className="d-flex align-items-center gap-2">
                                {(searchTerm || roleFilter) && (
                                  <Button 
                                    variant="light-solid" 
                                    size="sm" 
                                    onClick={() => { setSearchTerm(''); setRoleFilter(''); }}
                                    className="d-flex align-items-center"
                                  >
                                    <i className="bi bi-x-lg"></i> <span className="d-none d-md-inline-block ms-1">clear</span>
                                  </Button>
                                )}
                                <Form.Group>
                                  <Form.Select value={roleFilter} size="sm" onChange={e => setRoleFilter(e.target.value)}>
                                    <option value="">All</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                  </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                  <Form.Control
                                  type="text"
                                  placeholder="Search..."
                                  value={searchTerm}
                                  size="sm"
                                  onChange={e => setSearchTerm(e.target.value)}
                                />
                                </Form.Group> 
                              </div>
                            )}
                            {tab.key === 'administrative assistant' && (
                              <div className="d-flex align-items-center gap-2">
                                 {searchTerm && (
                                  <Button 
                                    variant="light-solid" 
                                    size="sm" 
                                    onClick={() => setSearchTerm('')}
                                    className="d-flex align-items-center"
                                  >
                                    <i className="bi bi-x-lg"></i> <span className="d-none d-md-inline-block ms-1">clear</span>
                                  </Button>
                                )}
                                <Form.Group>
                                  <Form.Control
                                  type="text"
                                  placeholder="Search..."
                                  value={searchTerm}
                                  size="sm"
                                  onChange={e => setSearchTerm(e.target.value)}
                                />
                                </Form.Group>
                              </div>
                            )}
                            {filtered.length > 0 && (
                            <div className="d-flex align-items-center gap-3">
                              <Form.Select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                size="sm"
                              >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                              </Form.Select>
                            </div>
                            )}
                            </div>
                            </Card.Header>
                            {resendAlert && (
                              <div className="px-4 pt-2">
                                <Alert
                                  variant={resendAlert.variant}
                                  dismissible
                                  onClose={() => setResendAlert(null)}
                                  className="mb-0"
                                >
                                  {resendAlert.message}
                                </Alert>
                              </div>
                            )}
                            <Card.Body className="p-0">

                       
                            {/* Table for this role */}
                            <Table responsive hover className="mb-0">
                              <thead>
                                <tr className="custom-header-bg">
                                  <th className="px-4">Name</th>                                  
                                  <th>User ID</th>
                                  <th>Gender</th>
                                  <th>Email</th>
                                  {tab.key === 'resident' && <th>PGY Year</th>}
                                  {tab.key === 'graduated' && <th>Graduation Date</th>}
                                  {tab.key === 'graduated' && <th>Final PGY</th>}
                                  {tab.key === 'attending' && <th>Specialty</th>}
                                  {tab.key === 'medical student' && <th>Year of School</th>}
                                  <th>Last Login</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginated.length === 0 && (
                                  <tr>
                                    <td colSpan="100%" className="px-3 text-center text-muted py-4">
                                      No {tab.label.toLowerCase()} found. <br />
                                      <span>Click <b>+ Add User</b> to add a {tab.label.toLowerCase().slice(0, -1)}.</span>
                                    </td>
                                  </tr>
                                )}
                                {paginated.map(user => (
                                  <tr key={user.id} style={{cursor:'pointer'}}
                                    onClick={e => {
                                      // Exclude clicks on Status or Actions columns
                                      const statusCol = e.target.closest('td.status-col');
                                      const actionsCol = e.target.closest('td.actions-col');
                                      if (statusCol || actionsCol) return;
                                      if (tab.key === 'resident') navigate(`/resident-profile/${user.id}`);
                                      // Don't navigate for graduated users since they're no longer active
                                    }}>
                                    <td className="px-4"><div className="d-flex align-items-center gap-2"><UserAvatar user={user} size={32} alt="" />{capitalizeName(user.firstName)} {capitalizeName(user.lastName)}</div></td>
                                    <td>{user.id}</td>
                                    <td>{user.gender || '-'}</td>
                                    <td>{user.email}</td>
                                    {tab.key === 'resident' && <td><span className={`badge bg-${getPGYBadgeColor(user.year)}`}>PGY-{user.year}</span></td>}
                                    {tab.key === 'graduated' && <td>{user.graduationDate ? new Date(user.graduationDate).toLocaleDateString() : '-'}</td>}
                                    {tab.key === 'graduated' && <td><span className={`badge bg-${getPGYBadgeColor(user.year)}`}>PGY-{user.year}</span></td>}
                                    {tab.key === 'attending' && <td>{user.specialty || '-'}</td>}
                                    {tab.key === 'medical student' && <td>{user.yearOfSchool || '-'}</td>}
                                    <td>{user.lastLogin ? formatLastLogin(user.lastLogin) : 'Never'}</td>
                                    <td className="status-col">
                                      {tab.key === 'graduated' ? (
                                        <Badge className="rounded-2" bg="success">
                                          Graduated
                                        </Badge>
                                      ) : (
                                        <Badge className="rounded-2" bg={isProfileComplete(user) ? 'success' : 'secondary'}>
                                          {isProfileComplete(user) ? 'Complete' : 'Incomplete'}
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="actions-col">
                                      <Dropdown align="end">
                                        <Dropdown.Toggle variant="light-lite" size="sm" className="no-arrow"><i className="bi bi-three-dots-vertical"></i></Dropdown.Toggle>
                                        <Dropdown.Menu>
                                          {tab.key === 'graduated' ? (
                                            <>
                                              <Dropdown.Item disabled>View Details</Dropdown.Item>
                                              <Dropdown.Item disabled>Edit (Graduated)</Dropdown.Item>
                                              <Dropdown.Item className="text-danger" onClick={() => { setUserToDelete(user); setShowDeleteModal(true); }}>Delete</Dropdown.Item>
                                            </>
                                          ) : (
                                            <>
                                              <Dropdown.Item onClick={() => { 
                                                // Store the original PGY level for comparison
                                                setEditingUser({ 
                                                  ...user, 
                                                  originalYear: user.year 
                                                }); 
                                                setShowModal(true); 
                                              }}>Edit</Dropdown.Item>
                                              {canResendExistingProfileInvite(user) && (
                                                <Dropdown.Item
                                                  disabled={resendingInvite?.userId === user.id}
                                                  onClick={() => handleResendProfileInvite(user, 'existing')}
                                                >
                                                  {resendingInvite?.userId === user.id && resendingInvite?.mode === 'existing' ? (
                                                    <>
                                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                      Sending...
                                                    </>
                                                  ) : (
                                                    'Resend invite'
                                                  )}
                                                </Dropdown.Item>
                                              )}
                                              {canResendNewProfileInvite(user) && (
                                                <Dropdown.Item
                                                  disabled={resendingInvite?.userId === user.id}
                                                  onClick={() => handleResendProfileInvite(user, 'new')}
                                                >
                                                  {resendingInvite?.userId === user.id && resendingInvite?.mode === 'new' ? (
                                                    <>
                                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                      Sending...
                                                    </>
                                                  ) : (
                                                    'Resend new invite'
                                                  )}
                                                </Dropdown.Item>
                                              )}
                                              <Dropdown.Item className="text-danger" onClick={() => { setUserToDelete(user); setShowDeleteModal(true); }}>Delete</Dropdown.Item>
                                            </>
                                          )}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>    
                            </Card.Body>
                            </Card>
                          </Tab.Pane>
                        ))}
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>

                {/* Pagination */}
                {filtered.length > 0 && (
              
                  <Row className="mt-3 mb-3">
                    <Col lg={2} md={2} sm={12}></Col>
                    <Col lg={10} md={10} sm={12}>
                      <div className="d-flex justify-content-end align-items-center">
                      
                        
                        {totalPages > 1 && (
                          <Pagination size="sm" className="mb-0">
                            {/*<Pagination.First 
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                            />*/}
                            <Pagination.Prev 
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            />
                            
                            {/* Show page numbers */}
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNumber;
                              if (totalPages <= 5) {
                                pageNumber = i + 1;
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i;
                              } else {
                                pageNumber = currentPage - 2 + i;
                              }
                              
                              return (
                                <Pagination.Item
                                  key={pageNumber}
                                  active={pageNumber === currentPage}
                                  onClick={() => handlePageChange(pageNumber)}
                                >
                                  {pageNumber}
                                </Pagination.Item>
                              );
                            })}
                            
                            <Pagination.Next 
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            />
                            {/*<Pagination.Last 
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                            />*/}
                          </Pagination>
                        )}
                      </div>                  
                    </Col>
                  </Row>

                )}
              </div>

              {/* Edit User Modal */}
              <RBModal show={showModal} onHide={() => setShowModal(false)} centered>
                <RBModal.Header closeButton className="border-bottom-0 px-4">
                  <RBModal.Title className="w-50"> 
                    <div className="d-flex flex-column justify-content-start align-items-start">
                      <span>Edit User</span>
                      <div className="text-hl"></div>
                    </div>
          </RBModal.Title>
                </RBModal.Header>
                <RBModal.Body className="px-4">
                  {editingUser && (
                    <Form id="editUserForm" onSubmit={async (e) => {
                      e.preventDefault();
                      const token = localStorage.getItem("token");
                      
                      // Prepare the update data
                      const updateData = { ...editingUser };
                      
                      // If this is a PGY level change for a resident, ensure we have the changeReason
                      if (editingUser.role === 'resident' && editingUser.year !== undefined) {
                        const oldYear = editingUser.originalYear || editingUser.year;
                        const levelDifference = Math.abs(editingUser.year - oldYear);
                        
                        // Check if PGY level exceeds maximum
                        if (editingUser.year > maxPGYLevel) {
                          alert(`Cannot exceed maximum PGY level (${maxPGYLevel}) for this program.`);
                          return;
                        }
                        
                        if (levelDifference > 1 && !updateData.changeReason) {
                          alert('Significant PGY level changes require a detailed reason. Please provide a reason before saving.');
                          return;
                        }
                      }
                      
                      try {
                        const res = await fetch(
                          `${API_ENDPOINTS.DIRECTOR}/edit-user/${editingUser.id}`,
                          {
                            method: "PUT",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify(updateData),
                          }
                        );
                        
                        if (res.ok) {
                          setShowModal(false);
                          window.location.reload();
                        } else {
                          const errorData = await res.json();
                          console.error("Failed to edit user:", errorData);
                          
                          // Handle specific validation errors
                          if (errorData.details && errorData.details.valid === false) {
                            alert(`Validation Error: ${errorData.message}\n\nDetails: ${JSON.stringify(errorData.details, null, 2)}`);
                          } else {
                            alert("Failed to update user: " + (errorData.message || "Unknown error"));
                          }
                        }
                      } catch (err) {
                        console.error("Failed to edit user", err);
                        alert("Failed to update user. Please try again.");
                      }
                    }}>
                      <Row>
                        <Col lg={6}>
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="First Name">
                        <Form.Control
                          value={editingUser.firstName}
                          onChange={e => setEditingUser({ ...editingUser, firstName: e.target.value })}
                          required
                        />
                      </FloatingLabel>
                      </Form.Group> 
                      </Col>
                      <Col lg={6}>
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="Last Name">
                        <Form.Control
                          value={editingUser.lastName}
                          onChange={e => setEditingUser({ ...editingUser, lastName: e.target.value })}
                          required
                        />
                        </FloatingLabel>
                      </Form.Group>
                      </Col>
                      <Col lg={12}>
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="Email">
                        <Form.Control
                          value={editingUser.email}
                          onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                          required
                        />
                        </FloatingLabel>
                      </Form.Group>
                      </Col>
                     
                      
                      <Col lg={6}>
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="Date of Birth">
                        <Form.Control
                          type="date"
                          value={editingUser.dob || ""}
                          onChange={e => setEditingUser({ ...editingUser, dob: e.target.value })}
                        />
                        </FloatingLabel>
                        </Form.Group>
                      </Col>
                      <Col lg={6}>  
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="Gender">
                        <Form.Select
                          value={editingUser.gender || ''}
                          onChange={e => setEditingUser({ ...editingUser, gender: e.target.value })}
                        >
                          <option value="">Select...</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </Form.Select>
                        </FloatingLabel>
                      </Form.Group>
                      </Col>
                      <Col lg={6}>
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="Phone Number">
                        <Form.Control
                          value={editingUser.phone || ""}
                          onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })}
                        />
                        </FloatingLabel>
                      </Form.Group>
                      </Col>
                      {editingUser.role === 'resident' && (
                        <>
                        <Col lg={6}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="PGY Year">
                            <Form.Select
                              value={editingUser.year || ''}
                              onChange={e => {
                                const newYear = parseInt(e.target.value);
                                const oldYear = editingUser.originalYear || editingUser.year || 1;
                                const levelDifference = Math.abs(newYear - oldYear);
                                
                                // Check if the new PGY level exceeds the maximum
                                if (newYear > maxPGYLevel) {
                                  alert(`Cannot exceed maximum PGY level (${maxPGYLevel}) for this program.`);
                                  return;
                                }
                                
                                // Check if this is a significant change (more than 1 level)
                                if (levelDifference > 1) {
                                  const reason = prompt(`This is a significant PGY level change (${oldYear} → ${newYear}). Please provide a detailed reason (minimum 10 characters):`);
                                  if (!reason || reason.trim().length < 10) {
                                    alert('Significant PGY level changes require a detailed reason (minimum 10 characters).');
                                    return;
                                  }
                                  setEditingUser({ 
                                    ...editingUser, 
                                    year: newYear,
                                    changeReason: reason.trim()
                                  });
                                } else {
                                  const academicYearStart = editingUser.academicYearStart || calculateAcademicYearStart(newYear);
                                  const calculatedGraduationYear = calculateGraduationYear(newYear, academicYearStart);
                                  setEditingUser({ 
                                    ...editingUser, 
                                    year: newYear,
                                    graduationYear: calculatedGraduationYear,
                                    academicYearStart: academicYearStart
                                  });
                                }
                              }}
                              required
                            >
                              <option value="">Select Year</option>
                              {pgyLevelOptions
                                .filter(option => option.value <= maxPGYLevel)
                                .map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                            </Form.Select>
                            </FloatingLabel>
                            {editingUser.changeReason && (
                              <Form.Text className="text-info">
                                Change reason: {editingUser.changeReason}
                              </Form.Text>
                            )}
                            {editingUser.originalYear && editingUser.year && Math.abs(editingUser.year - editingUser.originalYear) > 1 && (
                              <Form.Text className="text-warning">
                                ⚠️ Significant PGY level change detected. This will be logged for audit purposes.
                              </Form.Text>
                            )}
                            {editingUser.year && editingUser.year > maxPGYLevel && (
                              <Form.Text className="text-danger">
                                ❌ Cannot exceed maximum PGY level ({maxPGYLevel}) for this program.
                              </Form.Text>
                            )}
                          </Form.Group>
                          </Col>

                          <Col lg={6}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="Academic Year Start">
                            <Form.Control
                              type="date"
                              value={editingUser.academicYearStart || ''}
                              onChange={e => {
                                const newAcademicYearStart = e.target.value;
                                const calculatedGraduationYear = calculateGraduationYear(editingUser.year, newAcademicYearStart);
                                setEditingUser({
                                  ...editingUser,
                                  academicYearStart: newAcademicYearStart,
                                  graduationYear: calculatedGraduationYear
                                });
                              }}
                            />
                            <Form.Text className="text-muted">
                              Date when resident started their current academic year (July 1st)
                            </Form.Text>
                            </FloatingLabel>
                          </Form.Group>
                          </Col>
                          <Col lg={6}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="Expected Graduation Year">
                            <Form.Control
                              type="number"
                              min={new Date().getFullYear()}
                              max={new Date().getFullYear() + 10}
                              value={editingUser.graduationYear || ''}
                              onChange={e => setEditingUser({ ...editingUser, graduationYear: e.target.value })}
                              placeholder="e.g., 2028"
                            />
                            <Form.Text className="text-muted">
                              Expected graduation year based on current PGY level
                            </Form.Text>
                            </FloatingLabel>
                          </Form.Group>
                          </Col>
                        </>
                      )}
                      
                      {editingUser.role === 'attending' && (
                        <Col lg={6}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="Specialty">
                          <Form.Select
                            value={editingUser.specialty || ''}
                            onChange={e => setEditingUser({ ...editingUser, specialty: e.target.value })}
                            required
                          >
                            <option value="">Select Specialty</option>
                            {specialties.map(spec => (
                              <option key={spec} value={spec}>{spec}</option>
                            ))}
                          </Form.Select>
                          </FloatingLabel>
                          </Form.Group>
                          </Col>
                      )}
                      
                      {editingUser.role === 'medical student' && (
                        <Col lg={6}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="Year of School">
                          <Form.Select
                            value={editingUser.yearOfSchool || ''}
                            onChange={e => setEditingUser({ ...editingUser, yearOfSchool: e.target.value })}
                            required
                          >
                            <option value="">Select Year</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                          </Form.Select>
                          </FloatingLabel>
                          </Form.Group>
                          </Col>
                      )}
                      </Row>
                    </Form>
                  )}                      

                </RBModal.Body>                  
                <RBModal.Footer className="px-4 border-top-0" >
                        <Button variant="light" size="sm" onClick={() => setShowModal(false)}>
                          Cancel
                        </Button>
                        <Button 
                          variant="primary" 
                          size="sm"
                          type="submit" 
                          form="editUserForm"
                          disabled={
                            editingUser && 
                            editingUser.role === 'resident' && 
                            editingUser.year && 
                            editingUser.year > maxPGYLevel
                          }
                        >
                          Save Changes
                        </Button>
                      </RBModal.Footer>
              </RBModal>

              {/* Invite User Modal */}
              <RBModal show={showInviteModal} onHide={() => setShowInviteModal(false)} centered size={inviteStep === 2 ? "xl" : "md"}>
                <RBModal.Header closeButton className="border-bottom-0 px-4 gap-3">
                  <RBModal.Title className="w-100"> 
                    <div className="d-flex flex-column justify-content-start align-items-start">
                      <span>Invite {inviteRole.charAt(0).toUpperCase() + inviteRole.slice(1)}</span>
                      <div className="text-hl"></div>
                    </div>
                  </RBModal.Title>
                  
                  {/* Progress indicator for multi-step modal */}
                  {inviteRole === 'resident' && (
                    <div className="w-100">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className={`step-indicator ${inviteStep >= 1 ? 'active' : ''}`}>
                          <span className="step-label">Basic Info</span>
                        </div>
                        <div className={`step-line ${inviteStep >= 2 ? 'active' : ''}`}></div>
                        <div className={`step-indicator ${inviteStep >= 2 ? 'active' : ''}`}>
                          <span className="step-label">Documents</span>
                        </div>
                      </div>
                    </div>
                  )}
                </RBModal.Header>
                <RBModal.Body className="px-4">
                  {inviteStep === 1 ? (
                    <Form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }}>
                    <Row>
                      <Col md={6}>
                    <Form.Group className="mb-3">
                      <FloatingLabel controlId="floatingInput" label="First Name" className="required-field">
                      <Form.Control
                        value={inviteForm.firstName}
                        onChange={e => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                        required
                      />
                      </FloatingLabel>
                      </Form.Group>
                      </Col>
                      {/*<Col md={6}>
                    <Form.Group className="mb-3">
                      <FloatingLabel controlId="floatingInput" label="Middle Name">
                      <Form.Control
                        value={inviteForm.middleName}
                        onChange={e => setInviteForm({ ...inviteForm, middleName: e.target.value })}
                      />
                      </FloatingLabel>
                      </Form.Group>
                      </Col>*/}
                      <Col md={6}>
                    <Form.Group className="mb-3">
                      <FloatingLabel controlId="floatingInput" label="Last Name" className="required-field">
                      <Form.Control
                        value={inviteForm.lastName}
                        onChange={e => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                        required
                      />
                      </FloatingLabel>
                      </Form.Group>
                      </Col>
                      {(inviteRole=== 'resident' || inviteRole === 'medical student') && (
                        <Col md={8}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="Email" className="required-field">
                              <Form.Control
                                type="email"
                                value={inviteForm.email}
                                onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                                required
                              />
                            </FloatingLabel>
                          </Form.Group>
                        </Col>
                      )}
                      {(inviteRole === 'attending' || inviteRole === 'administrative assistant') && (
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <FloatingLabel controlId="floatingInput" label="Email" className="required-field">
                              <Form.Control
                                type="email"
                                value={inviteForm.email}
                                onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                                required
                              />
                            </FloatingLabel>
                          </Form.Group>
                        </Col>
                      )}
                    
                    {inviteRole === 'resident' && (
                      <>
                        <Col md={4}>
                        <Form.Group className="mb-3">
                          <FloatingLabel controlId="floatingInput" label="PGY Year">
                          <Form.Select
                            value={inviteForm.year}
                            onChange={e => {
                              const newYear = e.target.value;
                              const academicYearStart = inviteForm.academicYearStart || getCurrentAcademicYearStart();
                              const calculatedGraduationYear = calculateGraduationYear(newYear, academicYearStart);
                              setInviteForm({ 
                                ...inviteForm, 
                                year: newYear,
                                graduationYear: calculatedGraduationYear
                              });
                            }}
                            required
                          >
                            <option value="">Select Year</option>
                            {pgyLevelOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-3">
                          <FloatingLabel controlId="floatingInput" label="Academic Year Start">
                          <Form.Control
                            type="date"
                            value={inviteForm.academicYearStart}
                            onChange={e => {
                              const newAcademicYearStart = e.target.value;
                              const calculatedGraduationYear = calculateGraduationYear(inviteForm.year, newAcademicYearStart);
                              setInviteForm({
                                ...inviteForm,
                                academicYearStart: newAcademicYearStart,
                                graduationYear: calculatedGraduationYear
                              });
                            }}
                            placeholder="YYYY-MM-DD"
                          />
                          <Form.Text className="text-muted">
                            Date when resident started their current academic year (July 1st)
                          </Form.Text>
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-3">
                          <FloatingLabel controlId="floatingInput" label="Expected Graduation Year">
                          <Form.Control
                            type="number"
                            min={new Date().getFullYear()}
                            max={new Date().getFullYear() + 10}
                            value={inviteForm.graduationYear}
                            onChange={e => setInviteForm({ ...inviteForm, graduationYear: e.target.value })}
                            placeholder="e.g., 2028"
                          />
                          <Form.Text className="text-muted">
                            Expected graduation year based on current PGY level
                          </Form.Text>
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                    {inviteRole === 'medical student' && (
                      <Col md={4}>
                      <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingInput" label="Year of School">
                        <Form.Select
                          value={inviteForm.yearOfSchool}
                          onChange={e => setInviteForm({ ...inviteForm, yearOfSchool: e.target.value })}
                          required
                        >
                          <option value="">Select Year</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                        </Form.Select>
                        </FloatingLabel>
                        </Form.Group>
                      </Col>
                    )}
                    {inviteRole === 'attending' && (
                      <>
                        <Col md={6}>
                        <Form.Group className="mb-3">
                          <FloatingLabel controlId="floatingInput" label="Specialty">
                          <Form.Select
                            value={inviteForm.specialty}
                            onChange={e => setInviteForm({ ...inviteForm, specialty: e.target.value })}
                            required
                          >
                            <option value="">Select Specialty</option>
                            {specialties.map((spec) => (
                              <option key={spec} value={spec}>{spec}</option>
                            ))}
                          </Form.Select>
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-2">
                          <FloatingLabel controlId="floatingInput" label="Department">
                          <Form.Control
                            value={inviteForm.department}
                            onChange={e => setInviteForm({ ...inviteForm, department: e.target.value })}
                            required
                          />
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-2">
                          <FloatingLabel controlId="floatingInput" label="Position Title">
                          <Form.Control
                            value={inviteForm.positionTitle}
                            onChange={e => setInviteForm({ ...inviteForm, positionTitle: e.target.value })}
                            required
                          />
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="checkbox"
                            label="Board Certified"
                            id="boardCertifiedInvite"
                            checked={inviteForm.boardCertified}
                            onChange={e => setInviteForm({ ...inviteForm, boardCertified: e.target.checked })}
                          />
                        </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-2">
                          <FloatingLabel controlId="floatingInput" label="Start Date">
                          <Form.Control
                            type="date"
                            value={inviteForm.startDate}
                            onChange={e => setInviteForm({ ...inviteForm, startDate: e.target.value })}
                            required
                          />
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                        <Form.Group className="mb-2">
                          <FloatingLabel controlId="floatingInput" label="End Date (optional)">
                          <Form.Control
                            type="date"
                            value={inviteForm.endDate}
                            onChange={e => setInviteForm({ ...inviteForm, endDate: e.target.value })}
                          />
                          </FloatingLabel>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                    
                    {/* Document Assignment Checkbox - Only for residents */}
                    {inviteRole === 'resident' && (
                      <Row className="mt-3">
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Check
                              type="checkbox"
                              label="Assign Document Requirements"
                              id="assignDocuments"
                              checked={assignDocuments}
                              onChange={(e) => setAssignDocuments(e.target.checked)}
                            />
                            <Form.Text className="text-muted">
                              Select this to assign onboarding documents and packages that the resident must complete before accessing the application.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                    
                    {inviteError && <Alert variant="danger">{inviteError}</Alert>}
                    {inviteSuccess && <Alert variant="success">{inviteSuccess}</Alert>}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <Button variant="light" size="sm" onClick={() => setShowInviteModal(false)}>
                        Cancel
                      </Button>
                      <Button variant="primary" size="sm" type="submit">
                        {inviteRole === 'resident' ? (assignDocuments ? 'Next: Select Documents' : 'Send Invite') : 'Send Invite'}
                      </Button>
                    </div>
                    </Row>
                  </Form>
                ) : (
                  // Step 2: Document Assignment
                  <div>
                    <Alert variant="info" className="mb-3">
                      <h5 className="mb-2">Select Document Requirements</h5>
                    <p className="text-muted">Choose the onboarding packages and documents that the resident must complete before accessing the application. You can assign packages and documents individually or assign both together.</p>
                    </Alert>
                    {loadingDocuments ? (
                      <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading onboarding data...</p>
                      </div>
                    ) : (
                      <>
                        {/* Packages Section */}
                        <div className="mb-4">
                          <h6 className="mb-3">
                            <i className="bi bi-folder-plus me-2"></i>
                            Select Onboarding Packages
                          </h6>
                          {packages.length === 0 ? (
                            <Alert variant="secondary">
                              <strong>No packages available.</strong> You need to create onboarding packages first.
                              <br />
                              <Button 
                                variant="link" 
                                className="p-0 mt-2"
                                onClick={() => window.open('/onboarding', '_blank')}
                              >
                                Go to Onboarding Management
                              </Button>
                            </Alert>
                          ) : (
                            <div className="row">
                              {packages.map(pkg => (
                                <div key={pkg.id} className="col-md-6 mb-3">
                                  <Card 
                                    className={`h-100 ${selectedDocumentItems.packages.includes(pkg.id) ? 'border-primary' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handlePackageToggle(pkg.id)}
                                  >
                                    <Card.Body>
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <div className="d-flex flex-row align-items-center gap-2 mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className="d-flex align-items-center justify-content-center py-1 px-2 rounded-2 bg-warning">
                                              <i className="bi bi-folder-fill text-warning"></i>
                                              </div>
                                              <h6 className="mb-0">{pkg.name}</h6>
                                            </div>
                                            {pkg.category && (
                                            <Badge bg="secondary" className="me-2">{pkg.category}</Badge>
                                          )}
                                          </div>
                                          {pkg.description && (
                                            <p className="text-muted small mb-2">{pkg.description}</p>
                                          )}
                                          
                                        </div>
                                        <Form.Check
                                          type="checkbox"
                                          checked={selectedDocumentItems.packages.includes(pkg.id)}
                                          onChange={() => handlePackageToggle(pkg.id)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Documents Section */}
                        <div className="mb-4">
                          <h6 className="mb-3">
                            <i className="bi bi-file-earmark-plus me-2"></i>
                            Select Individual Documents
                          </h6>
                          {documents.length === 0 ? (
                            <Alert variant="secondary">
                              <strong>No documents available.</strong> You need to upload onboarding documents first.
                              <br />
                              <Button 
                                variant="link" 
                                className="p-0 mt-2"
                                onClick={() => window.open('/onboarding', '_blank')}
                              >
                                Go to Onboarding Management
                              </Button>
                            </Alert>
                          ) : (
                            <div className="row">
                              {documents.map(doc => (
                                <div key={doc.id} className="col-md-6 mb-3">
                                  <Card 
                                    className={`h-100 ${selectedDocumentItems.documents.includes(doc.id) ? 'border-primary' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleDocumentToggle(doc.id)}
                                  >
                                    <Card.Body>
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <div className="d-flex flex-row align-items-center gap-2">
                                            <div className="d-flex align-items-center gap-2">
                                              <div className={`d-flex align-items-center justify-content-center py-1 px-2 rounded-2 ${getFileIconBg(doc.fileName, doc.mimeType)}`}>
                                              <i className={getFileIcon(doc.fileName, doc.mimeType)}></i>
                                              </div>
                                              <h6 className="mb-0">{doc.title}</h6>
                                            </div>
                                            {doc.category && (
                                            <Badge bg="info" className="me-2">{doc.category}</Badge>
                                          )}         
                                          <small className="text-muted d-block">
                                          ({(doc.fileSize / 1024 / 1024).toFixed(2)} MB)
                                          </small>
                                          </div>
                                        </div>
                                        <Form.Check
                                          type="checkbox"
                                          checked={selectedDocumentItems.documents.includes(doc.id)}
                                          onChange={() => handleDocumentToggle(doc.id)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                    </Card.Body>
                                  </Card>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Selection Summary */}
                        {(selectedDocumentItems.packages.length > 0 || selectedDocumentItems.documents.length > 0) && (
                          <Alert variant="success" className="mb-3">
                            <i className="bi bi-check-circle me-2"></i>
                            <strong>Selected:</strong> {selectedDocumentItems.packages.length} packages, {selectedDocumentItems.documents.length} documents
                          </Alert>
                        )}
                        
                        {(selectedDocumentItems.packages.length === 0 && selectedDocumentItems.documents.length === 0) && (
                          <Alert variant="warning" className="mb-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            <strong>No items selected.</strong> Please select at least one package or document to assign to the resident.
                          </Alert>
                        )}

                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <Button variant="outline-secondary" size="sm" onClick={handleBackToBasicInfo}>
                            <i className="bi bi-arrow-left me-2"></i>
                            Back
                          </Button>
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={handleInviteSubmit}
                            disabled={selectedDocumentItems.packages.length === 0 && selectedDocumentItems.documents.length === 0}
                          >
                            <i className="bi bi-send me-2"></i>
                            Send Invite with Documents
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                </RBModal.Body>
              </RBModal>

              {/* Delete User Confirmation Modal */}
              <RBModal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <RBModal.Header closeButton className="border-bottom-0 px-4">
                  <RBModal.Title className="w-100">
                    <div className="d-flex flex-column justify-content-start align-items-start">
                      <span>Confirm Delete</span>
                      <div className="text-hl-danger"></div>
                    </div>
                  </RBModal.Title>
                </RBModal.Header>
                <RBModal.Body className="px-4">
                  {userToDelete && (
                    <>
                      <p>Are you sure you want to delete <strong>{capitalizeName(userToDelete.firstName)} {capitalizeName(userToDelete.lastName)}</strong>?</p>
                      <p>This action cannot be undone. All data associated with this user will be permanently removed.</p>
                      {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                    </>
                  )}
                </RBModal.Body>
                <RBModal.Footer className="px-4 border-top-0">
                  <Button variant="secondary" size="sm" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                    Cancel
                  </Button>
                  <Button variant="danger" size="sm" onClick={handleDeleteUser} disabled={isDeleting}>
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete User'
                    )}
                  </Button>
                </RBModal.Footer>
              </RBModal>
            </div>
          </>
        )}
        </Tab.Pane>
        
        <Tab.Pane eventKey="delegations" active={activeTab === "delegations"}>
          <DelegationsManager users={users} />
        </Tab.Pane>
        
        <Tab.Pane eventKey="Promotion" active={activeTab === "Promotion"}>
          <ResidentPromotionManager />
        </Tab.Pane>

        <Tab.Pane eventKey="announcements" active={activeTab === "announcements"}>
          {announcementSuccess && (
            <Alert variant="success" onClose={() => setAnnouncementSuccess('')} dismissible>
              {announcementSuccess}
            </Alert>
          )}    
          <Card className="shadow-sm border-0">
            <Card.Header className="border-0 px-4 py-3">
              <div className="d-flex flex-column">
                <h6 className="mb-0 fw-semibold">Announcements</h6>
                <p className="text-muted small mb-0">showing {announcements.length} announcements</p>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {announcements.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-megaphone-fill text-muted mb-3" style={{ fontSize: '3rem' }} />
                  <h6 className="text-muted">No announcements yet</h6>
                  <p className="text-muted">Create your first announcement to get started.</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      resetAnnouncementForm();
                      setShowAnnouncementModal(true);
                    }}
                  >
                    <i className="bi bi-plus-lg me-1" />
                    Create Announcement
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr className="custom-header-bg">
                      <th className="px-4">Title</th>
                      <th>Type</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Created By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map((announcement) => {
                      const today = new Date().toISOString().split('T')[0];
                      const isActive = announcement.isActive && 
                        announcement.startDate <= today && 
                        announcement.endDate > today;
                      
                      return (
                        <tr key={announcement.id}>
                          <td className="px-4">
                            <div>
                              {announcement.title}
                              <div className="text-muted small fw-regular mt-1 d-none d-md-block">
                                {announcement.content.length > 60 
                                  ? `${announcement.content.substring(0, 60)}...` 
                                  : announcement.content
                                }
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getTypeColor(announcement.type)}>
                              {announcement.type}
                            </Badge>
                          </td>
                          <td>{formatDate(announcement.startDate)}</td>
                          <td>{formatDate(announcement.endDate)}</td>
                          <td>
                            <Badge className="rounded-2" bg={isActive ? 'success' : 'secondary'}>
                              {isActive ? 'Active' : 'Inactive'} 
                            </Badge>
                          </td>
                          <td>
                            {announcement.creator?.firstName && announcement.creator?.lastName ? 
                              `${capitalizeName(announcement.creator.firstName)} ${capitalizeName(announcement.creator.lastName)}` : 
                              'Unknown'
                            }
                          </td>
                          <td>
                              <Dropdown align="end">
                                <Dropdown.Toggle variant="light-lite" size="sm" className="no-arrow"><i className="bi bi-three-dots-vertical"></i></Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => editAnnouncement(announcement)}>
                                    <i className="bi bi-pencil"></i> Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDeleteAnnouncement(announcement.id)}>
                                    <i className="bi bi-trash-fill"></i> Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            </Card>
        </Tab.Pane>
        
        <Tab.Pane eventKey="subscriptions" active={activeTab === "subscriptions"}>
          {subscriptionLoading || !subscriptionData ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading subscription details...</p>
            </div>
          ) : (
            <>
              {(subscriptionPercents.storage >= 85 || subscriptionPercents.faculty >= 85 || subscriptionPercents.residents >= 85) && (
                <Alert variant="warning" className="mb-3">
                  <div className="fw-semibold mb-1">Usage is approaching plan limits</div>
                  <div className="small">
                    Residents: {subscriptionPercents.residents}% | Faculty: {subscriptionPercents.faculty}% | Storage: {subscriptionPercents.storage}%
                  </div>
                </Alert>
              )}
              {subscriptionData.invoices.some((invoice) => invoice.status.toLowerCase() === 'open') && (
                <Alert variant="info" className="mb-3">
                  One or more invoices are currently open. Review billing history to avoid service interruptions.
                </Alert>
              )}

              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
                <div>
                  <h4 className="mb-1">Payments & Subscriptions</h4>
                  <div className="text-muted">Manage your program billing profile and usage.</div>
                </div>
                <Button variant="outline-primary" disabled>
                  Manage subscription
                </Button>
              </div>

              <Card className="shadow-sm border-0 mb-4">
                <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <div className="text-muted small mb-1">Upcoming Payment</div>
                    <h6 className="mb-1">
                      {subscriptionData.upcomingPayment.amount} due on {subscriptionData.upcomingPayment.chargeDate}
                    </h6>
                    <div className="small text-muted">
                      Charged to {subscriptionData.upcomingPayment.method} • Invoice {subscriptionData.upcomingPayment.invoiceId}
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openInvoiceModal({
                        ...subscriptionData.invoices[0],
                        id: subscriptionData.upcomingPayment.invoiceId,
                        status: 'Pending Payment',
                        date: subscriptionData.upcomingPayment.chargeDate,
                        issueDate: subscriptionData.upcomingPayment.chargeDate,
                        dueDate: subscriptionData.upcomingPayment.chargeDate,
                      })}
                    >
                      View upcoming invoice
                    </Button>
                    <Button size="sm" variant="success">
                      Pay now
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Row className="g-3 mb-4">
                <Col lg={4}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Body>
                      <div className="text-muted small mb-1">Current Plan</div>
                      <h5 className="mb-1">{subscriptionData.plan.name}</h5>
                      <div className="mb-2">{subscriptionData.plan.price}</div>
                      <Badge bg="success">{subscriptionData.plan.status}</Badge>
                      <hr />
                      <div className="small text-muted">Billing cadence</div>
                      <div className="mb-2">{subscriptionData.plan.cadence}</div>
                      <div className="small text-muted">Renewal date</div>
                      <div className="mb-2">{subscriptionData.plan.renewalDate}</div>
                      <div className="small text-muted">Included seats</div>
                      <div>{subscriptionData.plan.seatsIncluded}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Body>
                      <div className="text-muted small mb-1">Usage Data</div>
                      <h5 className="mb-3">Current period</h5>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Residents</span>
                        <strong>{subscriptionData.usage.residents}</strong>
                      </div>
                      <ProgressBar
                        className="mb-3"
                        now={subscriptionPercents.residents}
                        variant={subscriptionPercents.residents >= 90 ? 'danger-solid' : 'success-solid'}
                      />
                      <div className="d-flex justify-content-between mb-2">
                        <span>Faculty</span>
                        <strong>{subscriptionData.usage.faculty}</strong>
                      </div>
                      <ProgressBar
                        className="mb-3"
                        now={subscriptionPercents.faculty}
                        variant={subscriptionPercents.faculty >= 90 ? 'danger-solid' : 'primary-solid'}
                      />
                      <div className="d-flex justify-content-between mb-2">
                        <span>Storage used</span>
                        <strong>
                          {subscriptionData.usage.storageUsedGb} GB / {subscriptionData.usage.storageLimitGb} GB
                        </strong>
                      </div>
                      <ProgressBar
                        now={subscriptionPercents.storage}
                        label={`${subscriptionPercents.storage}%`}
                        variant={subscriptionPercents.storage >= 90 ? 'danger-solid' : 'orange-solid'}
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Body>
                      <div className="text-muted small mb-1">Payment Method</div>
                      <h5 className="mb-2">
                        {subscriptionData.paymentMethod.brand} ending in {subscriptionData.paymentMethod.last4}
                      </h5>
                      <div className="mb-3">
                        Expires {subscriptionData.paymentMethod.expMonth}/{subscriptionData.paymentMethod.expYear}
                      </div>
                      <div className="small text-muted">Billing email</div>
                      <div className="mb-3">{subscriptionData.paymentMethod.billingEmail}</div>
                      <Button size="sm" variant="outline-secondary" disabled>
                        Update payment method
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-3">
                <Col xl={5}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white">
                      <h6 className="mb-0">Billing Details</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <div className="text-muted small">Program</div>
                        <div>{subscriptionData.billingContact.programName}</div>
                      </div>
                      <div className="mb-2">
                        <div className="text-muted small">Institution</div>
                        <div>{subscriptionData.billingContact.institutionName}</div>
                      </div>
                      <div className="mb-2">
                        <div className="text-muted small">Billing contact</div>
                        <div>{subscriptionData.billingContact.contactName}</div>
                      </div>
                      <div className="mb-2">
                        <div className="text-muted small">Email</div>
                        <div>{subscriptionData.billingContact.email}</div>
                      </div>
                      <div className="mb-2">
                        <div className="text-muted small">Phone</div>
                        <div>{subscriptionData.billingContact.phone}</div>
                      </div>
                      <div>
                        <div className="text-muted small">Address</div>
                        <div>{subscriptionData.billingContact.address}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={7}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Billing History</h6>
                      <Button variant="outline-secondary" size="sm">
                        Export
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-2 mb-3">
                        <Col md={5}>
                          <Form.Control
                            size="sm"
                            placeholder="Search invoice ID, date, amount"
                            value={invoiceSearch}
                            onChange={(event) => setInvoiceSearch(event.target.value)}
                          />
                        </Col>
                        <Col md={4}>
                          <Form.Select
                            size="sm"
                            value={invoiceStatusFilter}
                            onChange={(event) => setInvoiceStatusFilter(event.target.value)}
                          >
                            <option value="all">All statuses</option>
                            <option value="paid">Paid</option>
                            <option value="open">Open</option>
                            <option value="failed">Failed</option>
                          </Form.Select>
                        </Col>
                      </Row>
                      <Table responsive hover className="mb-0">
                        <thead>
                          <tr>
                            <th className="ps-3">Invoice</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th className="pe-3 text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSubscriptionInvoices.length > 0 ? (
                            filteredSubscriptionInvoices.map((invoice) => {
                              const normalizedStatus = invoice.status.toLowerCase();
                              return (
                                <tr key={invoice.id}>
                                  <td className="ps-3">
                                    <Button
                                      variant="link"
                                      className="p-0 text-decoration-none"
                                      onClick={() => openInvoiceModal(invoice)}
                                    >
                                      {invoice.id}
                                    </Button>
                                  </td>
                                  <td>{invoice.date}</td>
                                  <td>{invoice.amount}</td>
                                  <td>
                                    <Badge bg={invoiceStatusVariant[normalizedStatus] || 'secondary'}>{invoice.status}</Badge>
                                  </td>
                                  <td className="pe-3 text-end">
                                    <div className="d-inline-flex gap-2">
                                      <Button size="sm" variant="outline-primary" onClick={() => openInvoiceModal(invoice)}>
                                        View
                                      </Button>
                                      <Button size="sm" variant="outline-secondary">
                                        Download
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-4 text-muted">
                                No invoices found for the current filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Tab.Pane>

        <Tab.Pane eventKey="settings" active={activeTab === "settings"}>
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header className="border-0 p-4" style={{backgroundColor: 'var(--bs-body-bg)'}}>
              <h5 className="mb-0">Case Log Feedback Settings</h5>
              <p className="text-muted mb-0">Configure automatic feedback evaluations for case logs</p>
            </Card.Header>
            <Card.Body className="p-4">
              {settingsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading settings...</p>
                </div>
              ) : (
                <>
                  {settingsError && (
                    <Alert variant="danger" onClose={() => setSettingsError('')} dismissible>
                      {settingsError}
                    </Alert>
                  )}
                  
                  {settingsSuccess && (
                    <Alert variant="success" onClose={() => setSettingsSuccess('')} dismissible>
                      {settingsSuccess}
                    </Alert>
                  )}

                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-4">
                          <Form.Label>Enable Automatic Feedback</Form.Label>
                          <Form.Check
                            type="switch"
                            checked={feedbackSettings.isEnabled}
                            onChange={(e) => setFeedbackSettings(prev => ({ ...prev, isEnabled: e.target.checked }))}
                            label="Send feedback evaluations when residents add case logs"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {feedbackSettings.isEnabled && (
                      <>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-4">
                              <Form.Label>Template</Form.Label>
                              <Form.Select
                                value={feedbackSettings.feedbackTemplateId || ''}
                                onChange={(e) => setFeedbackSettings(prev => ({ 
                                  ...prev, 
                                  feedbackTemplateId: e.target.value ? parseInt(e.target.value) : null 
                                }))}
                              >
                                <option value="">Select a template</option>
                                {feedbackTemplates.map(template => (
                                  <option key={template.id} value={template.id}>
                                    {template.title}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Choose a saved template to use for case log feedback
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-4">
                              <Form.Label>Roles That Trigger Feedback</Form.Label>
                              <div className="d-flex flex-wrap gap-3">
                                {['Intern', 'Secondary Resident', 'Primary Resident', 'Chief Resident'].map(role => (
                                  <Form.Check
                                    key={role}
                                    type="checkbox"
                                    id={`role-${role}`}
                                    checked={feedbackSettings.rolesToTrigger.includes(role)}
                                    onChange={() => handleRoleToggle(role)}
                                    label={role}
                                    className="me-3"
                                  />
                                ))}
                              </div>
                              <Form.Text className="text-muted">
                                Select which resident roles should trigger feedback evaluations
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <div className="d-flex gap-2">
                              <Button 
                                variant="primary" 
                                size="sm"
                                onClick={saveFeedbackSettings}
                                disabled={settingsSaving}
                              >
                                {settingsSaving ? 'Saving...' : 'Save Settings'}
                              </Button>
                              <Button 
                                variant="secondary" 
                                size="sm"
                                onClick={() => {
                                  fetchFeedbackSettings();
                                  setSettingsError('');
                                  setSettingsSuccess('');
                                }}
                                disabled={settingsSaving}
                              >
                                Reset
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Form>
                </>
              )}
            </Card.Body>
          </Card>
        </Tab.Pane>
        
        </Tab.Content>
      </Container>

      {/* Announcement Modal */}
      <RBModal 
        show={showAnnouncementModal} 
        onHide={() => {
          setShowAnnouncementModal(false);
          resetAnnouncementForm();
        }}
        centered
      >
        <RBModal.Header closeButton className="border-bottom-0 px-4">
          <RBModal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>
                {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
              </span>
              <div className="text-hl"></div>
            </div>
          </RBModal.Title>
        </RBModal.Header>
        <RBModal.Body className="px-4">
          {announcementError && (
            <Alert variant="danger" onClose={() => setAnnouncementError('')} dismissible>
              {announcementError}
            </Alert>
          )}
          
          <Form onSubmit={handleAnnouncementSubmit}>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Title" className="required-field">
                  <Form.Control
                    type="text"
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                    required
                    placeholder="Enter announcement title"
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Type" className="required-field">
                  <Form.Select
                    value={announcementForm.type}
                    onChange={(e) => setAnnouncementForm({...announcementForm, type: e.target.value})}
                    required
                  >
                    <option value="general">General</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                    <option value="info">Info</option>
                  </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Start Date" className="required-field">
                  <Form.Control
                    type="date"
                    value={announcementForm.startDate}
                    onChange={(e) => setAnnouncementForm({...announcementForm, startDate: e.target.value})}
                    required
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="End Date" className="required-field">
                  <Form.Control
                    type="date"
                    value={announcementForm.endDate}
                    onChange={(e) => setAnnouncementForm({...announcementForm, endDate: e.target.value})}
                    required
                  />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Content" className="required-field">
              <Form.Control
                as="textarea"
                rows={4}
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                required
                placeholder="Enter announcement content"
                style={{minHeight: '100px'}}
              />
              </FloatingLabel>
            </Form.Group>
          </Form>
        </RBModal.Body>
        <RBModal.Footer className="px-4 border-top-0">
          <Button 
            variant="secondary" 
            size="sm"
            onClick={() => {
              setShowAnnouncementModal(false);
              resetAnnouncementForm();
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleAnnouncementSubmit}
            disabled={isSubmittingAnnouncement}
          >
            {isSubmittingAnnouncement ? 'Saving...' : (editingAnnouncement ? 'Update' : 'Create')}
          </Button>
        </RBModal.Footer>
      </RBModal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Delete announcement</span>
              <div className="text-hl-danger"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          {confirmMessage}
        </Modal.Body>
        <Modal.Footer className="px-4 border-top-0">
          <Button variant="secondary" size="sm" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={async () => { setShowConfirmModal(false); await confirmAction(); }}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!selectedInvoice} onHide={closeInvoiceModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Invoice Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && subscriptionData && (
            <div className="border rounded p-3 p-md-4 bg-white">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <img src="/images/asset5.svg" alt="App logo" style={{ height: 16 }} />
                  </div>
                  <h5 className="mt-3 mb-1">Invoice {selectedInvoice.id}</h5>
                  <div className="small text-muted">
                    Issue Date: {selectedInvoice.issueDate} • Due Date: {selectedInvoice.dueDate}
                  </div>
                </div>
                <div className="text-end">
                  <Badge bg={invoiceStatusVariant[selectedInvoice.status.toLowerCase()] || 'secondary'} className="mb-2">
                    {selectedInvoice.status}
                  </Badge>
                  <div className="fw-semibold fs-5">{selectedInvoice.amount}</div>
                </div>
              </div>

              <Row className="g-4 mb-4">
                <Col md={6}>
                  <div className="text-muted small">Issue For</div>
                  <div className="fw-semibold">{selectedInvoice.invoiceFor}</div>
                  <div className="text-muted small mt-3">Issued By</div>
                  <div className="fw-semibold">{selectedInvoice.issuedBy}</div>
                </Col>
                <Col md={6}>
                  <div className="text-muted small">Payment Details</div>
                  <div className="fw-semibold">
                    {subscriptionData.paymentMethod.brand} ending in {subscriptionData.paymentMethod.last4}
                  </div>
                  <div>{subscriptionData.paymentMethod.billingEmail}</div>
                  <div className="mt-3 text-muted small">Payment Term</div>
                  <div className="fw-semibold">{selectedInvoice.paymentTerm}</div>
                </Col>
              </Row>

              <Table responsive className="mb-3 custom-modern-responsive">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items.map((item) => (
                    <tr key={item.description}>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>{item.rate}</td>
                      <td className="text-end">{item.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-end me-2 mt-4">
                <div style={{ maxWidth: 320, width: '100%', flexShrink: 1 }}>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Subtotal</span>
                    <span>{selectedInvoice.amount}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Tax</span>
                    <span>$0.00</span>
                  </div>
                  <div className="d-flex justify-content-between fw-semibold border-top pt-2 mt-2">
                    <span>Total</span>
                    <span>{selectedInvoice.amount}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      
      <style>{`
        .step-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          opacity: 0.5;
          transition: opacity 0.3s ease;
        }
        
        .step-indicator.active {
          opacity: 1;
        }
        
        .step-number {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: #e9ecef;
          color: var(--gray-700);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .step-indicator.active .step-number {
          background-color: var(--blue);
          color: white;
        }
        
        .step-label {
          font-size: 0.875rem;
          color: var(--gray-700);
        }
        
        .step-indicator.active .step-label {
          color: var(--blue);
          font-weight: 500;
        }
        
        .step-line {
          flex: 1;
          height: 2px;
          background-color: var(--border-bg);
          margin: 0 10px;
          transition: background-color 0.3s ease;
        }
        
        .step-line.active {
          background-color: var(--blue);
        }
      `}</style>
    </div>
  );
};

export default UsersPage;
